import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

export const defaultConfig: AxiosRequestConfig = {
  responseType: 'json',
};

export const axiosFactory = (baseURL: string, timeout: number) => {
  const axiosInstance = axios.create({
    baseURL,
    timeout,
  });
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => Promise.reject(error)
  );

  const customGet = <T = any, R = AxiosResponse<T>>(
    url: string,
    params?: any,
    config?: AxiosRequestConfig
  ) =>
    (axiosInstance.get<T>(url, {
      ...defaultConfig,
      ...config,
      params,
    }) as unknown) as Promise<R>;

  const customPost = <T = any, R = AxiosResponse<T>>(
    csrfToken: string,
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ) =>
    (axiosInstance.post<T>(url, data, {
      ...defaultConfig,
      ...config,
      headers: {
        'X-CSRF-TOKEN': csrfToken,
      },
    }) as unknown) as Promise<R>;

  const customPut = <T = any, R = AxiosResponse<T>>(
    csrfToken: string,
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<R> =>
    (axiosInstance.put<T>(url, data, {
      ...defaultConfig,
      ...config,
      headers: {
        'X-CSRF-TOKEN': csrfToken,
      },
    }) as unknown) as Promise<R>;

  const customDelete = <T = any, R = AxiosResponse<T>>(
    csrfToken: string,
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> =>
    (axiosInstance.delete<T>(url, {
      ...defaultConfig,
      ...config,
      headers: {
        'X-CSRF-TOKEN': csrfToken,
      },
    }) as unknown) as Promise<R>;

  return {
    ...axiosInstance,
    get: customGet,
    post: customPost,
    put: customPut,
    delete: customDelete,
  };
};

/** Axios Factory to call external resources
 * */
export const axiosExternalRecourseFactory = (baseURL: string, timeout: number) => {
  const axiosExternalRecourseInstance = axios.create({
    baseURL,
    timeout,
  });

  axiosExternalRecourseInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (axios.isCancel(error)) {
        // do error handling
      }
      return Promise.reject(error);
    }
  );

  const externalRecourseGet = <T = any, R = AxiosResponse<T>>(
    url: string,
    params?: any,
    config?: AxiosRequestConfig
  ) =>
    (axiosExternalRecourseInstance.get<T>(url, {
      ...defaultConfig,
      ...config,
      params,
    }) as unknown) as Promise<R>;

  const externalRecoursePut = <T = any, R = AxiosResponse<T>>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<R> =>
    (axiosExternalRecourseInstance.put<T>(url, data, {
      ...config,
    }) as unknown) as Promise<R>;

  const externalRecoursePost = <T = any, R = AxiosResponse<T>>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<R> =>
    (axiosExternalRecourseInstance.post<T>(url, data, {
      ...defaultConfig,
      ...config,
    }) as unknown) as Promise<R>;

  return {
    ...axiosExternalRecourseInstance,
    get: externalRecourseGet,
    post: externalRecoursePost,
    put: externalRecoursePut,
  };
};
